import React from 'react';

import styled from 'styled-components';
import {
  FlexColumn,
  FlexRow,
  formatHash,
  HashLength,
  useMatchMedia,
} from '@make-software/cspr-ui';
import { Tooltip, BodyText } from '@block-explorer/ui';
import { CopyButton } from '../copy-button/copy-button';
import { Cspr } from '../cspr/cspr';
import { Trans, useTranslation } from 'react-i18next';
import Big from 'big.js';
import { PrecisionCase } from '../../utils/currency';

export const ValuesRow = styled(FlexRow)(({ theme }) => ({
  height: 36,
  borderRadius: theme.borderRadius.base,
  padding: '8px 16px',
  backgroundColor: theme.styleguideColors.backgroundPrimary,
  border: theme.border.base,
}));

const StyledFlexColumn = styled(FlexColumn)<{ disabled?: boolean }>(
  ({ disabled }) => ({
    ...(disabled && {
      opacity: 0.5,
    }),
  })
);

const BalanceText = styled(BodyText)(({ theme }) => ({
  textAlign: 'right',
}));

export interface AccountInfoRowProps {
  publicKey: string;
  label: string;
  accountBalance: string | null;
  loading: boolean;
  error: string | null;
  accountEmpty: boolean;
  disabled?: boolean;
}

export function AccountInfoRow(props: AccountInfoRowProps) {
  const { t } = useTranslation();
  const { publicKey, label, accountEmpty, accountBalance, loading, error } =
    props;

  const responsiveHashSize = useMatchMedia(
    [HashLength.TINY, HashLength.SMALL, HashLength.SMALL, HashLength.SMALL],
    []
  );

  const emptyBalance =
    accountEmpty ||
    (!loading && accountBalance != null && Big(accountBalance).lte('0'));

  return (
    <StyledFlexColumn disabled={props.disabled} gap={4}>
      <FlexRow justify="space-between">
        <BodyText scale={'sm'} size={1}>
          {label}
        </BodyText>
        <BodyText scale={'sm'} size={1}>
          <Trans t={t}>Balance</Trans>
        </BodyText>
      </FlexRow>

      <ValuesRow justify="space-between" align="center">
        {publicKey && (
          <>
            <FlexRow align="center" itemsSpacing={4}>
              <Tooltip title={publicKey}>
                <BodyText size={3} monotype>
                  {formatHash(publicKey, responsiveHashSize)}
                </BodyText>
              </Tooltip>
              <CopyButton value={publicKey} type={'full'} />
            </FlexRow>
            <BalanceText size={3} monotype>
              {emptyBalance ? (
                <Cspr motes={'0'} precisionCase={PrecisionCase.deployCost} />
              ) : loading ? (
                t('Loading...')
              ) : error != null ? (
                error
              ) : (
                <Cspr
                  motes={accountBalance}
                  precisionCase={PrecisionCase.deployCost}
                />
              )}
            </BalanceText>
          </>
        )}
      </ValuesRow>
    </StyledFlexColumn>
  );
}

export default AccountInfoRow;
